/**
 * ServiceProfilesPage
 *
 * Full CRUD for ServiceProfiles — no Redux, direct API calls via serviceProfileApi.
 *
 * Features:
 *  - Paginated listing with active-only filter
 *  - Create / Edit modal
 *    · Name, Make, Model, Year, Applicable KM
 *    · Searchable, ordered ProfileWorkItem picker (add / remove, reorder)
 *  - Activate / Deactivate toggle
 *  - Delete with confirmation modal
 *  - Toast feedback on every operation
 */

import React, { useCallback, useEffect, useState } from 'react';
import MainLayout from './Layout.jsx';
import {
    getServiceProfilesApi,
    createServiceProfileApi,
    updateServiceProfileApi,
    activateServiceProfileApi,
    deactivateServiceProfileApi,
    deleteServiceProfileApi,
    getProfileWorkItemsForPickerApi,
    provisionStarterServiceProfilesApi,
    getApprovalModesApi,
} from '../services/serviceProfileApi';

// ─── Constants ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 10;

// ─── Tiny shared UI primitives ────────────────────────────────────────────────

const Spinner = ({ size = 5 }) => (
    <div className={`animate-spin rounded-full w-${size} h-${size} border-2 border-gray-200 border-t-emerald-600`} />
);

const Badge = ({ active }) => (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
        active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'
    }`}>
        {active ? 'Active' : 'Inactive'}
    </span>
);

const EmptyRow = ({ cols, message }) => (
    <tr>
        <td colSpan={cols} className="py-12 text-center text-gray-400 text-sm">{message}</td>
    </tr>
);

// ─── Toast ────────────────────────────────────────────────────────────────────

const Toast = ({ message, type = 'success', onClose }) => {
    useEffect(() => {
        const t = setTimeout(onClose, 3500);
        return () => clearTimeout(t);
    }, [onClose]);

    const colours = type === 'success'
        ? 'bg-emerald-600 text-white'
        : 'bg-red-600 text-white';

    return (
        <div className={`fixed bottom-6 right-6 z-[100] flex items-center gap-3 px-5 py-3.5 rounded-2xl shadow-xl ${colours} max-w-sm`}>
            <span className="text-sm font-medium">{message}</span>
            <button onClick={onClose} className="ml-auto opacity-70 hover:opacity-100 transition-opacity bg-transparent border-none cursor-pointer text-white">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>
        </div>
    );
};

// ─── Delete Confirm Modal ─────────────────────────────────────────────────────

const DeleteConfirmModal = ({ title, message, isLoading, error, onConfirm, onCancel }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onCancel} />
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="px-6 py-5 border-b border-gray-100">
                <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            </div>
            <div className="px-6 py-5">
                <p className="text-sm text-gray-600">{message}</p>
                {error && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{error}</div>
                )}
            </div>
            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3">
                <button
                    onClick={onCancel}
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors bg-transparent border-none cursor-pointer"
                >
                    Cancel
                </button>
                <button
                    onClick={onConfirm}
                    disabled={isLoading}
                    className="inline-flex items-center gap-2 px-5 py-2 bg-red-600 text-white text-sm font-semibold rounded-xl hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                >
                    {isLoading && <Spinner size={4} />}
                    Delete
                </button>
            </div>
        </div>
    </div>
);

// ─── Pagination ───────────────────────────────────────────────────────────────

const Pagination = ({ page, totalPages, totalElements, onPageChange }) => {
    if (totalPages <= 1) return null;
    return (
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 text-sm text-gray-500">
            <span>{totalElements} total</span>
            <div className="flex items-center gap-2">
                <button
                    onClick={() => onPageChange(page - 1)}
                    disabled={page === 0}
                    className="px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                    Previous
                </button>
                <span className="px-2 font-medium text-gray-700">
                    Page {page + 1} of {totalPages}
                </span>
                <button
                    onClick={() => onPageChange(page + 1)}
                    disabled={page >= totalPages - 1}
                    className="px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                    Next
                </button>
            </div>
        </div>
    );
};

// ─── Work Item Picker ─────────────────────────────────────────────────────────

/**
 * Shows all available active ProfileWorkItems.
 * Selected items appear in an ordered "Added" list that can be reordered
 * via up/down arrows or removed individually.
 */
const WorkItemPicker = ({ allWorkItems, selectedUuids, onChange }) => {
    const [search, setSearch] = useState('');

    const selected  = selectedUuids
        .map(u => allWorkItems.find(w => w.uuid === u))
        .filter(Boolean);

    const available = allWorkItems.filter(w =>
        !selectedUuids.includes(w.uuid) &&
        w.action.toLowerCase().includes(search.toLowerCase())
    );

    const add = (uuid) => onChange([...selectedUuids, uuid]);

    const remove = (uuid) => onChange(selectedUuids.filter(u => u !== uuid));

    const move = (index, dir) => {
        const next = [...selectedUuids];
        const swap = index + dir;
        if (swap < 0 || swap >= next.length) return;
        [next[index], next[swap]] = [next[swap], next[index]];
        onChange(next);
    };

    return (
        <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">
                Work Items <span className="text-red-500">*</span>
                <span className="ml-1.5 text-xs font-normal text-gray-400">
                    ({selected.length} selected)
                </span>
            </label>

            {/* Selected / ordered list */}
            {selected.length > 0 && (
                <div className="border border-emerald-200 rounded-xl divide-y divide-gray-100 bg-emerald-50/40">
                    {selected.map((wi, idx) => (
                        <div key={wi.uuid} className="flex items-center gap-2 px-3 py-2.5">
                            {/* Reorder */}
                            <div className="flex flex-col gap-0.5">
                                <button
                                    type="button"
                                    onClick={() => move(idx, -1)}
                                    disabled={idx === 0}
                                    className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-20 bg-transparent border-none cursor-pointer"
                                    title="Move up"
                                >
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                    </svg>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => move(idx, 1)}
                                    disabled={idx === selected.length - 1}
                                    className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-20 bg-transparent border-none cursor-pointer"
                                    title="Move down"
                                >
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                            </div>

                            {/* Order number */}
                            <span className="w-5 text-xs font-semibold text-emerald-600 text-center flex-shrink-0">
                                {idx + 1}
                            </span>

                            {/* Label */}
                            <span className="flex-1 text-sm text-gray-800 font-medium truncate">{wi.action}</span>

                            {/* Cost / hours meta */}
                            <div className="hidden sm:flex items-center gap-3 text-xs text-gray-400 flex-shrink-0">
                                {wi.labourCost != null && (
                                    <span>${Number(wi.labourCost).toFixed(2)}</span>
                                )}
                                {wi.estimatedHours != null && (
                                    <span>{wi.estimatedHours}h</span>
                                )}
                            </div>

                            {/* Remove */}
                            <button
                                type="button"
                                onClick={() => remove(wi.uuid)}
                                className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors bg-transparent border-none cursor-pointer flex-shrink-0"
                                title="Remove"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* Available work items to add */}
            <div>
                <div className="relative mb-2">
                    <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                    </svg>
                    <input
                        type="text"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Search work items to add…"
                        className="w-full pl-9 pr-3.5 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                    />
                </div>

                <div className="max-h-44 overflow-y-auto border border-gray-200 rounded-xl divide-y divide-gray-100">
                    {available.length === 0 ? (
                        <p className="py-4 text-center text-xs text-gray-400">
                            {search ? 'No work items match your search.' : 'All active work items have been added.'}
                        </p>
                    ) : (
                        available.map(wi => (
                            <button
                                key={wi.uuid}
                                type="button"
                                onClick={() => add(wi.uuid)}
                                className="w-full flex items-center gap-3 px-3.5 py-2.5 hover:bg-gray-50 transition-colors text-left bg-transparent border-none cursor-pointer"
                            >
                                <svg className="w-4 h-4 text-emerald-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                                <span className="flex-1 text-sm text-gray-700 font-medium truncate">{wi.action}</span>
                                <div className="hidden sm:flex items-center gap-3 text-xs text-gray-400 flex-shrink-0">
                                    {wi.labourCost != null && (
                                        <span>${Number(wi.labourCost).toFixed(2)}</span>
                                    )}
                                    {wi.estimatedHours != null && (
                                        <span>{wi.estimatedHours}h</span>
                                    )}
                                </div>
                            </button>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

// ─── Service Profile Modal ────────────────────────────────────────────────────

const EMPTY_FORM = {
    name:          '',
    make:          '',
    model:         '',
    vehicleYear:   '',
    mileageKm:     '',
    approvalMode:  'WORK_ITEM',
    workItemUuids: [],
};

const ServiceProfileModal = ({ profile, allWorkItems, approvalModes, isBusy, operationError, onClose, onSubmit }) => {
    const isEdit = !!profile;

    const [form, setForm] = useState(() =>
        isEdit
            ? {
                name:          profile.name          ?? '',
                make:          profile.make          ?? '',
                model:         profile.model         ?? '',
                vehicleYear:   profile.vehicleYear   ?? '',
                mileageKm:     profile.mileageKm     ?? '',
                approvalMode:  profile.approvalMode   ?? 'WORK_ITEM',
                workItemUuids: profile.workItems?.map(w => w.uuid) ?? [],
            }
            : { ...EMPTY_FORM }
    );

    const set = (field) => (e) => setForm(p => ({ ...p, [field]: e.target.value }));

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit({
            name:          form.name.trim(),
            make:          form.make.trim()    || null,
            model:         form.model.trim()   || null,
            vehicleYear:   form.vehicleYear    ? parseInt(form.vehicleYear, 10)  : null,
            mileageKm:     form.mileageKm      ? parseInt(form.mileageKm, 10)    : null,
            approvalMode:  form.approvalMode,
            workItemUuids: form.workItemUuids,
        });
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] flex flex-col overflow-hidden">

                {/* Header */}
                <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
                    <h2 className="text-lg font-semibold text-gray-900">
                        {isEdit ? 'Edit Service Profile' : 'Create Service Profile'}
                    </h2>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors bg-transparent border-none cursor-pointer"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Body */}
                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto flex flex-col">
                    <div className="px-6 py-5 space-y-5 flex-1">

                        {/* Name */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                Profile Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={form.name}
                                onChange={set('name')}
                                placeholder="e.g. Premium Detail"
                                required
                                className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                            />
                            <p className="text-xs text-gray-500 mt-1.5">
                                A reusable bundle of work items. Vehicle details below are optional.
                            </p>
                        </div>

                        {/* Make + Model */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">Make</label>
                                <input
                                    type="text"
                                    value={form.make}
                                    onChange={set('make')}
                                    placeholder="e.g. Toyota"
                                    className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">Model</label>
                                <input
                                    type="text"
                                    value={form.model}
                                    onChange={set('model')}
                                    placeholder="e.g. Camry"
                                    className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                                />
                            </div>
                        </div>

                        {/* Year + Mileage */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                    Vehicle Year
                                </label>
                                <input
                                    type="number"
                                    value={form.vehicleYear}
                                    onChange={set('vehicleYear')}
                                    placeholder="e.g. 2019"
                                    min="1886"
                                    max="2100"
                                    className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                    kms
                                </label>
                                <input
                                    type="number"
                                    value={form.mileageKm}
                                    onChange={set('mileageKm')}
                                    placeholder="e.g. 60000"
                                    min="0"
                                    step="1000"
                                    className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                                />
                            </div>
                        </div>

                        {/* Approval Mode */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                Customer Approval Mode
                            </label>
                            <select
                                value={form.approvalMode}
                                onChange={set('approvalMode')}
                                className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none bg-white"
                            >
                                {approvalModes.map(mode => (
                                    <option key={mode} value={mode}>
                                        {mode === 'PROFILE' ? 'Profile — single authorisation for all items' : 'Work Item — confirm each item individually'}
                                    </option>
                                ))}
                            </select>
                            <p className="mt-1 text-xs text-gray-400">
                                {form.approvalMode === 'PROFILE'
                                    ? 'One email will be sent to the customer to approve all work items at once.'
                                    : 'Each work item can be sent individually for customer confirmation.'}
                            </p>
                        </div>

                        {/* Work Items Picker */}
                        <WorkItemPicker
                            allWorkItems={allWorkItems}
                            selectedUuids={form.workItemUuids}
                            onChange={uuids => setForm(p => ({ ...p, workItemUuids: uuids }))}
                        />

                        {/* Error */}
                        {operationError && (
                            <div className="p-3.5 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                                {operationError}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-end gap-3 flex-shrink-0">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors bg-transparent border-none cursor-pointer"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isBusy || form.workItemUuids.length === 0}
                            className="inline-flex items-center gap-2 px-5 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-xl hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                        >
                            {isBusy && <Spinner size={4} />}
                            {isEdit ? 'Save Changes' : 'Create Profile'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// ─── Profile Detail Expand Row ────────────────────────────────────────────────

const ProfileDetailRow = ({ profile, cols }) => (
    <tr className="bg-gray-50/80">
        <td colSpan={cols} className="px-6 py-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                Work Items ({profile.workItems?.length ?? 0})
            </p>
            {!profile.workItems?.length ? (
                <p className="text-sm text-gray-400">No work items attached.</p>
            ) : (
                <ol className="space-y-1.5 list-none">
                    {profile.workItems.map((wi, idx) => (
                        <li key={wi.uuid} className="flex items-center gap-3 text-sm">
                            <span className="w-5 h-5 flex items-center justify-center rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold flex-shrink-0">
                                {idx + 1}
                            </span>
                            <span className="font-medium text-gray-800">{wi.action}</span>
                            <span className="text-gray-400 text-xs flex-shrink-0">
                                {wi.labourCost != null && `$${Number(wi.labourCost).toFixed(2)}`}
                                {wi.labourCost != null && wi.estimatedHours != null && ' · '}
                                {wi.estimatedHours != null && `${wi.estimatedHours}h`}
                            </span>
                            {!wi.active && (
                                <span className="text-xs text-gray-400 italic">(inactive)</span>
                            )}
                        </li>
                    ))}
                </ol>
            )}
        </td>
    </tr>
);

// ─── Profiles Table ───────────────────────────────────────────────────────────

const ProfilesTable = ({ profiles, isLoading, expandedUuid, onToggleExpand, onEdit, onDelete, onToggleActive }) => {
    const cols = 6;

    return (
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="px-4 py-3 text-left font-semibold text-gray-600 w-8" />
                        <th className="px-4 py-3 text-left font-semibold text-gray-600">Name</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-600">Vehicle</th>
                        <th className="px-4 py-3 text-center font-semibold text-gray-600">Items</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-600">Status</th>
                        <th className="px-4 py-3 text-right font-semibold text-gray-600">Actions</th>
                    </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                    {isLoading ? (
                        <tr>
                            <td colSpan={cols} className="py-12 text-center">
                                <div className="flex justify-center"><Spinner size={6} /></div>
                            </td>
                        </tr>
                    ) : profiles.length === 0 ? (
                        <EmptyRow cols={cols} message="No service profiles found." />
                    ) : (
                        profiles.map(p => {
                            const isExpanded = expandedUuid === p.uuid;
                            const vehicleParts = [p.make, p.model, p.vehicleYear].filter(Boolean);
                            const vehicleLabel = vehicleParts.length
                                ? vehicleParts.join(' ')
                                : '—';
                            const kmLabel = p.mileageKm != null
                                ? `${p.mileageKm.toLocaleString()} km`
                                : null;

                            return (
                                <React.Fragment key={p.uuid}>
                                    <tr
                                        className={`group hover:bg-gray-50/60 transition-colors cursor-pointer ${isExpanded ? 'bg-gray-50/60' : ''}`}
                                        onClick={() => onToggleExpand(p.uuid)}
                                    >
                                        {/* Expand chevron */}
                                        <td className="px-4 py-3 text-gray-400">
                                            <svg
                                                className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                                                fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        </td>

                                        {/* Name */}
                                        <td className="px-4 py-3">
                                            <span className="font-semibold text-gray-900">{p.name}</span>
                                        </td>

                                        {/* Vehicle */}
                                        <td className="px-4 py-3 text-gray-600">
                                            <span>{vehicleLabel}</span>
                                            {kmLabel && (
                                                <span className="ml-2 text-xs text-gray-400">{kmLabel}</span>
                                            )}
                                        </td>

                                        {/* Item count */}
                                        <td className="px-4 py-3 text-center">
                                                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold">
                                                    {p.workItems?.length ?? 0}
                                                </span>
                                        </td>

                                        {/* Status */}
                                        <td className="px-4 py-3">
                                            <Badge active={p.active} />
                                        </td>

                                        {/* Actions */}
                                        <td className="px-4 py-3">
                                            <div
                                                className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                                onClick={e => e.stopPropagation()}
                                            >
                                                {/* Toggle active */}
                                                <button
                                                    onClick={() => onToggleActive(p)}
                                                    title={p.active ? 'Deactivate' : 'Activate'}
                                                    className="p-1.5 rounded-lg text-gray-400 hover:text-amber-600 hover:bg-amber-50 transition-colors bg-transparent border-none cursor-pointer"
                                                >
                                                    {p.active ? (
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                                        </svg>
                                                    ) : (
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                    )}
                                                </button>

                                                {/* Edit */}
                                                <button
                                                    onClick={() => onEdit(p)}
                                                    title="Edit"
                                                    className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors bg-transparent border-none cursor-pointer"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536M9 13l6.586-6.586a2 2 0 012.828 2.828L11.828 15H9v-2z" />
                                                    </svg>
                                                </button>

                                                {/* Delete */}
                                                <button
                                                    onClick={() => onDelete(p)}
                                                    title="Delete"
                                                    className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors bg-transparent border-none cursor-pointer"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>

                                    {/* Expandable detail row */}
                                    {isExpanded && (
                                        <ProfileDetailRow profile={p} cols={cols} />
                                    )}
                                </React.Fragment>
                            );
                        })
                    )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

const ServiceProfilesPage = () => {

    // ── List state ──
    const [profiles,      setProfiles]      = useState([]);
    const [isLoading,     setIsLoading]     = useState(true);
    const [pageError,     setPageError]     = useState(null);
    const [page,          setPage]          = useState(0);
    const [totalPages,    setTotalPages]    = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [activeOnly,    setActiveOnly]    = useState(false);
    const [expandedUuid,  setExpandedUuid]  = useState(null);

    // ── Work items for picker ──
    const [allWorkItems,      setAllWorkItems]      = useState([]);
    const [isLoadingWorkItems, setIsLoadingWorkItems] = useState(false);

    // ── Approval modes ──
    const [approvalModes,     setApprovalModes]     = useState(['PROFILE', 'WORK_ITEM']);

    // ── Modal state ──
    const [modalProfile,    setModalProfile]    = useState(null);   // null = closed, false = create, object = edit
    const [isModalOpen,     setIsModalOpen]     = useState(false);
    const [isBusy,          setIsBusy]          = useState(false);
    const [operationError,  setOperationError]  = useState(null);

    // ── Delete state ──
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [isDeleting,   setIsDeleting]   = useState(false);
    const [deleteError,  setDeleteError]  = useState(null);

    // ── Toast ──
    const [toast, setToast] = useState(null);

    // ── Starter profiles ──
    const [isProvisioning, setIsProvisioning] = useState(false);

    /**
     * Copies the bundled starter services into this workshop, then reloads.
     *
     * They arrive as ordinary rows — renamed, repriced and pruned like any other. The
     * count of work items is reported alongside, because the button also creates those
     * and a workshop that was not told would wonder where forty-three of them came from.
     */
    const handleAddStarterProfiles = useCallback(async () => {
        setIsProvisioning(true);
        setPageError(null);
        try {
            const result = await provisionStarterServiceProfilesApi();
            if (result.alreadyProvisioned) {
                setToast({ type: 'info', message: 'This workshop already has service profiles.' });
            } else if (result.created === 0) {
                // No bundled set for this organisation type — not an error, but silence
                // here would read as the button being broken.
                setToast({ type: 'info', message: 'No starter services are bundled for this workshop type.' });
            } else {
                const items = result.workItemsCreated > 0
                    ? ` and ${result.workItemsCreated} work items`
                    : '';
                // ⚠️ Kept even though the plan no longer truncates a starter run: the
                // server still reports it, and a future limit that DOES bite must not
                // present as a success with a quietly smaller number.
                setToast(result.stoppedAtPlanLimit
                    ? {
                        type: 'info',
                        message: `Added ${result.created} of ${result.available} starter services${items}. `
                            + 'Your plan allows no more service profiles — upgrade to add the rest.',
                    }
                    : {
                        type: 'success',
                        message: `Added ${result.created} starter service${result.created === 1 ? '' : 's'}${items}. Rename or edit them to suit.`,
                    });
            }
            await fetchProfiles(0, activeOnly);
        } catch (e) {
            setPageError(e.response?.data?.message ?? 'Failed to add starter services.');
        } finally {
            setIsProvisioning(false);
        }
    }, [activeOnly]);

    // ─── Data fetching ──────────────────────────────────────────────────────

    const fetchProfiles = useCallback(async (pg = 0, active = activeOnly) => {
        setIsLoading(true);
        setPageError(null);
        try {
            const data = await getServiceProfilesApi({ page: pg, size: PAGE_SIZE, activeOnly: active });
            setProfiles(data.content ?? []);
            setTotalPages(data.totalPages ?? 0);
            setTotalElements(data.totalSize ?? data.totalElements ?? 0);
            setPage(pg);
        } catch (err) {
            setPageError(err.response?.data?.message ?? err.message);
        } finally {
            setIsLoading(false);
        }
    }, [activeOnly]);

    const fetchAllWorkItems = useCallback(async () => {
        setIsLoadingWorkItems(true);
        try {
            const data = await getProfileWorkItemsForPickerApi();
            setAllWorkItems(data.content ?? []);
        } catch {
            // non-fatal — picker will just be empty
        } finally {
            setIsLoadingWorkItems(false);
        }
    }, []);

    const fetchApprovalModes = useCallback(async () => {
        try {
            const data = await getApprovalModesApi();
            setApprovalModes(data);
        } catch {
            // non-fatal — falls back to hardcoded defaults
        }
    }, []);

    useEffect(() => { fetchProfiles(0, activeOnly); }, [activeOnly]);  // eslint-disable-line
    useEffect(() => { fetchAllWorkItems(); }, [fetchAllWorkItems]);
    useEffect(() => { fetchApprovalModes(); }, [fetchApprovalModes]);

    // ─── Helpers ────────────────────────────────────────────────────────────

    const showToast = (message, type = 'success') => setToast({ message, type });

    const toggleExpand = (uuid) =>
        setExpandedUuid(prev => (prev === uuid ? null : uuid));

    // ─── Modal handlers ─────────────────────────────────────────────────────

    const openCreate = () => {
        setModalProfile(null);
        setOperationError(null);
        setIsModalOpen(true);
    };

    const openEdit = (p) => {
        setModalProfile(p);
        setOperationError(null);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        if (isBusy) return;
        setIsModalOpen(false);
        setModalProfile(null);
        setOperationError(null);
    };

    const handleSubmit = async (formData) => {
        setIsBusy(true);
        setOperationError(null);
        try {
            const isEdit = !!modalProfile;
            if (isEdit) {
                await updateServiceProfileApi(modalProfile.uuid, formData);
            } else {
                await createServiceProfileApi(formData);
            }
            closeModal();
            showToast(isEdit ? 'Service profile updated.' : 'Service profile created.');
            fetchProfiles(isEdit ? page : 0, activeOnly);
        } catch (err) {
            setOperationError(err.response?.data?.message ?? err.message);
        } finally {
            setIsBusy(false);
        }
    };

    // ─── Toggle active ──────────────────────────────────────────────────────

    const handleToggleActive = async (p) => {
        try {
            if (p.active) {
                await deactivateServiceProfileApi(p.uuid);
            } else {
                await activateServiceProfileApi(p.uuid);
            }
            showToast(`Profile ${p.active ? 'deactivated' : 'activated'}.`);
            fetchProfiles(page, activeOnly);
        } catch (err) {
            showToast(err.response?.data?.message ?? err.message, 'error');
        }
    };

    // ─── Delete ─────────────────────────────────────────────────────────────

    const openDelete = (p) => {
        setDeleteTarget(p);
        setDeleteError(null);
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        setIsDeleting(true);
        setDeleteError(null);
        try {
            await deleteServiceProfileApi(deleteTarget.uuid);
            setDeleteTarget(null);
            showToast('Service profile deleted.');
            const newPage = profiles.length === 1 && page > 0 ? page - 1 : page;
            fetchProfiles(newPage, activeOnly);
        } catch (err) {
            setDeleteError(err.response?.data?.message ?? err.message);
        } finally {
            setIsDeleting(false);
        }
    };

    // ─── Render ─────────────────────────────────────────────────────────────

    return (
        <MainLayout>
            {/* Full width, like every other page. Layout already gives its children
                the whole area beside the sidebar; a max-w-7xl mx-auto here centred a
                1280px column inside it and left a dead margin down both sides that no
                other screen has. */}
            <div className="p-6">

                {/* Page Header */}
                <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-display font-bold text-gray-900">
                            Service Profiles
                        </h1>
                        <p className="mt-1 text-sm text-gray-500">
                            Reusable service templates that bundle work items for a vehicle make / model.
                        </p>
                    </div>
                    <div className="flex items-center gap-2 self-start sm:self-auto flex-shrink-0">
                        {/* Only on an empty workshop. Once there are profiles the button
                            would be a no-op the server refuses, and offering it would
                            read as the product ignoring a click. */}
                        {!isLoading && profiles.length === 0 && (
                            <button
                                onClick={handleAddStarterProfiles}
                                disabled={isProvisioning}
                                className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-xl hover:bg-emerald-700 transition-colors disabled:opacity-50"
                            >
                                {isProvisioning ? <Spinner size={4} /> : (
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                    </svg>
                                )}
                                Add starter services
                            </button>
                        )}
                        <button
                            onClick={openCreate}
                            className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl transition-colors ${
                                !isLoading && profiles.length === 0
                                    ? 'bg-white text-emerald-700 border border-emerald-300 hover:bg-emerald-50'
                                    : 'bg-emerald-600 text-white hover:bg-emerald-700'
                            }`}
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            New Profile
                        </button>
                    </div>
                </div>

                {/* Toolbar */}
                <div className="mb-4 flex items-center gap-4">
                    <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={activeOnly}
                            onChange={e => {
                                setActiveOnly(e.target.checked);
                                setPage(0);
                            }}
                            className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                        />
                        Active only
                    </label>

                    {totalElements > 0 && (
                        <span className="text-xs text-gray-400 ml-auto">
                            {totalElements} profile{totalElements !== 1 ? 's' : ''}
                        </span>
                    )}
                </div>

                {/* Error */}
                {pageError && (
                    <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-center gap-3">
                        <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {pageError}
                        <button
                            onClick={() => fetchProfiles(page, activeOnly)}
                            className="ml-auto text-red-600 underline text-xs bg-transparent border-none cursor-pointer"
                        >
                            Retry
                        </button>
                    </div>
                )}

                {/* Table */}
                <ProfilesTable
                    profiles={profiles}
                    isLoading={isLoading}
                    expandedUuid={expandedUuid}
                    onToggleExpand={toggleExpand}
                    onEdit={openEdit}
                    onDelete={openDelete}
                    onToggleActive={handleToggleActive}
                />

                {/* Pagination */}
                <Pagination
                    page={page}
                    totalPages={totalPages}
                    totalElements={totalElements}
                    onPageChange={(pg) => fetchProfiles(pg, activeOnly)}
                />
            </div>

            {/* ── Create / Edit Modal ── */}
            {isModalOpen && (
                <ServiceProfileModal
                    profile={modalProfile}
                    allWorkItems={allWorkItems}
                    approvalModes={approvalModes}
                    isBusy={isBusy || isLoadingWorkItems}
                    operationError={operationError}
                    onClose={closeModal}
                    onSubmit={handleSubmit}
                />
            )}

            {/* ── Delete Confirm ── */}
            {deleteTarget && (
                <DeleteConfirmModal
                    title="Delete Service Profile"
                    message={`Are you sure you want to delete "${deleteTarget.name}"? This action cannot be undone.`}
                    isLoading={isDeleting}
                    error={deleteError}
                    onConfirm={confirmDelete}
                    onCancel={() => setDeleteTarget(null)}
                />
            )}

            {/* ── Toast ── */}
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}
        </MainLayout>
    );
};

export default ServiceProfilesPage;